import { Box, darken, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useUserStore } from '@roc/feature-app-core';
import { useDocumentStore } from '@roc/feature-documents';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import { LoanProcess, shouldHideTaskUploadTab } from '@roc/feature-utils';
import { TooltipOnOverflow } from '@roc/ui';
import { TodoTitleLink } from 'libs/feature-loan-details/src/loanDetails/components/cards/todoActions';
import { useGeneralTasksStore } from '../../hooks/useGeneralTasksStore';
import { CustomTaskDialog } from '../customTask/customTaskDialog';
import { useState } from 'react';
import { LineItemUpload } from '@roc/feature-loan-details';
import { GeneralTasksTab } from '../../types/generalTasksTypes';
import { ClosingConditionIcon, PostClosingConditionIcon } from './closingConditionIcons';

const useStyles = makeStyles(theme => ({
  link: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',

    '& a': {
      color: theme.palette.primary.dark,
      fontFamily: 'sans-serif',
      fontSize: '14px',
      fontWeight: 'bold',
    },
  },
}));

export const DocumentTitleCell = ({ row }) => {
  const { generalTasksStore } = useGeneralTasksStore();
  const { documentStore } = useDocumentStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const { userStore } = useUserStore();
  const classes = useStyles();
  const [openLineItemChat, setOpenLineItemChat] = useState(false);

  const document = documentStore.formatDocument(
    row.document,
    LoanProcess.UNDERWRITING
  );

  const section = document.borrowerId
    ? `${document.borrowerName} ${document.sectionName}`
    : document.collateralId
    ? `${document.collateralAddress} ${document.sectionName}`
    : document.sectionName;

  const getUrl = loanId => {
    return userStore.isNportalUser
      ? '/nportal/#/loan_information/' + loanId
      : loanRoutesConfig.loans(loanId).children.dashboard.url;
  };

  const init = async row => {
    const loanId = row.loanId;
    await documentStore.loanStore.fetchLoanDetails(loanId);
    generalTasksStore.setLoanId(loanId);
  };

  const onClose = () => {
    if (generalTasksStore.tab !== GeneralTasksTab.ALL_TASKS_FOR_LOAN) {
      generalTasksStore.setLoanId(null);
    }
    generalTasksStore.applyFilters();
  };

  const title = documentStore.getDisplayDocumentName(
    row?.document?.taskName,
    row?.document
  );

  const info = row?.document?.documentNameSecondLine || null;

  const formattedTitle = `${title} - ${section}`;

  return (
    <Box display="flex" alignItems="center">
      {row?.hasClosingCondition && (
        <Box pr={1}>
          <ClosingConditionIcon />
        </Box>
      )}
      {row?.hasPostClosingCondition && (
        <Box pr={1}>
          <PostClosingConditionIcon />
        </Box>
      )}
      <TooltipOnOverflow
        title={
          <>
            <b>{title}</b>
            <br />
            {section}
          </>
        }
      >
        <Typography className={classes.link}>
          <TodoTitleLink
            document={document}
            loanProcess={LoanProcess.UNDERWRITING}
            init={() => init(row)}
            showUploadLink={true}
            onClose={() => onClose()}
            onUploadCustom={() => {
              setOpenLineItemChat(true);
            }}
            propertyId={document?.collateralId}
          >
            {formattedTitle}
            {info ? ` - ${info}` : null}
          </TodoTitleLink>
        </Typography>
      </TooltipOnOverflow>
      {openLineItemChat && (
        <CustomTaskDialog
          title={document?.isParentTask ? '' : formattedTitle}
          taskId={document?.taskId}
          open={openLineItemChat}
          dialogContent={
            shouldHideTaskUploadTab(document) ? null : (
              <LineItemUpload document={document} expand={true} />
            )
          }
          handleClose={() => {
            setOpenLineItemChat(false);
            onClose();
          }}
        />
      )}
    </Box>
  );
};
